import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete, Checkbox, Chip, Stack, TextField, ToggleButton, ToggleButtonGroup, InputAdornment, Box, Typography,
} from '@mui/material';
import { Search, Map as MapIcon, ViewList, ViewAgenda, Opacity, Build, Fullscreen, FullscreenExit } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
    setSearch,
    setPanelMode,
    setDechetTypesFilter,
    setConteneurTypesFilter,
    setPatrimoineIdsFilter,
    setStatutsFilter,
    setMode,
} from '../../../modules/conteneurMap/conteneurMap.slice';
import {
    getConteneurTypeLabel,
    getDechetTypeLabel,
    getStatusColor,
    countStatuts,
    STATUT_FILTER_OPTIONS,
} from '../../../modules/conteneurMap/conteneurMap.helpers';
import { filterRowsByPatrimoineIds } from '../../../modules/conteneurMap/conteneurMap.slice';
import ChecklistToolbar from './ChecklistToolbar';

// Pseudo-option en tête de la liste des patrimoines : coche / décoche tout d'un clic.
const ALL_PATRIMOINES = { id: '__all__', label: 'Tout cocher' };

/** Deuxième ligne en mode « Lavage » : filtre par statut du dernier lavage, avec compteurs. */
function LavageStatutFilter({ rows }) {
    const dispatch = useDispatch();
    const statuts = useSelector((s) => s.conteneurMap.filters.statuts);
    const counts = useMemo(() => countStatuts(rows), [rows]);
    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
            <Typography variant="caption" color="text.secondary">Statut&nbsp;:</Typography>
            <ToggleButtonGroup
                size="small"
                value={statuts ?? []}
                onChange={(_e, val) => dispatch(setStatutsFilter(val))}
            >
                {STATUT_FILTER_OPTIONS.map((opt) => (
                    <ToggleButton key={opt.code} value={opt.code} sx={{ gap: 0.75, textTransform: 'none' }}>
                        <Box component="span" sx={{
                            width: 10, height: 10, borderRadius: '2px', bgcolor: getStatusColor(opt.code), display: 'inline-block',
                        }} />
                        {opt.label}
                        <Chip size="small" label={counts[opt.code] ?? 0} sx={{ height: 18, fontSize: 11 }} />
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Stack>
    );
}

/**
 * Toolbar de la vue Patrimoine v2.
 *
 * - `search` : debounce local (250ms) avant push dans le store.
 * - `dechetTypes` / `conteneurTypes` : ToggleButtonGroup multi-select. Les
 *   valeurs proposées sont dérivées des `rows` afin de ne montrer que les
 *   types réellement présents chez le client.
 * - Toggle Liste / Carte / Les deux.
 * - PAT-04 : sélecteur multi-patrimoines (R7, Autocomplete à cases, tous par défaut) et toggle
 *   Lavage / Maintenance ; 2ᵉ ligne selon le mode : filtre de statut de lavage (`LavageStatutFilter`)
 *   ou `ChecklistToolbar` en mode Maintenance. Pseudo-option « Tout cocher / Tout décocher » en tête
 *   des patrimoines (les filtres étant persistés, on ne recoche pas 20 patrimoines à la main).
 */
function ConteneurMapToolbar({ rows, actes = [], acteCourant = null, presentation = false, onTogglePresentation = null }) {
    const dispatch = useDispatch();
    const filters = useSelector((s) => s.conteneurMap.filters);
    const panelMode = useSelector((s) => s.conteneurMap.panelMode);
    const mode = useSelector((s) => s.conteneurMap.mode);

    const [searchInput, setSearchInput] = useState(filters.search ?? '');

    // Debounce search → store
    useEffect(() => {
        const t = setTimeout(() => {
            if (searchInput !== filters.search) dispatch(setSearch(searchInput));
        }, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);
    // Store → input (restauration des filtres persistés, reset).
    useEffect(() => { setSearchInput(filters.search ?? ''); }, [filters.search]);

    // Options dérivées des rows : uniquement les codes réellement présents
    const dechetOptions = useMemo(() => {
        const set = new Set();
        (rows || []).forEach((r) => { if (r.dechetType) set.add(r.dechetType); });
        return Array.from(set).sort().map((code) => ({ code, label: getDechetTypeLabel(code) || code }));
    }, [rows]);

    const conteneurOptions = useMemo(() => {
        const set = new Set();
        (rows || []).forEach((r) => { if (r.conteneurType) set.add(r.conteneurType); });
        return Array.from(set).sort().map((code) => ({ code, label: getConteneurTypeLabel(code) || code }));
    }, [rows]);

    // Patrimoines présents (id → libellé), triés ; `null` dans le store = tous cochés.
    const patrimoineOptions = useMemo(() => {
        const map = new Map();
        (rows || []).forEach((r) => {
            if (r.patrimoineId != null && !map.has(r.patrimoineId)) {
                map.set(r.patrimoineId, { id: r.patrimoineId, label: r.patrimoineLibelle || `Patrimoine ${r.patrimoineId}` });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [rows]);
    const patrimoineValue = useMemo(() => {
        if (!Array.isArray(filters.patrimoineIds)) return patrimoineOptions;
        const set = new Set(filters.patrimoineIds.map(String));
        return patrimoineOptions.filter((o) => set.has(String(o.id)));
    }, [filters.patrimoineIds, patrimoineOptions]);
    const allPatrimoinesChecked = patrimoineValue.length === patrimoineOptions.length;
    const onPatrimoinesChange = (_e, val, _reason, details) => {
        if (details?.option?.id === ALL_PATRIMOINES.id) {
            dispatch(setPatrimoineIdsFilter(allPatrimoinesChecked ? [] : null));
            return;
        }
        const ids = val.filter((o) => o.id !== ALL_PATRIMOINES.id);
        // Tout coché → null (pas de filtre, pas de fantômes).
        dispatch(setPatrimoineIdsFilter(ids.length === patrimoineOptions.length ? null : ids.map((o) => o.id)));
    };

    const rowsPatrimoines = useMemo(
        () => filterRowsByPatrimoineIds(rows || [], filters.patrimoineIds),
        [rows, filters.patrimoineIds]
    );

    return (
        <>
        <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{ mb: 1.5, mt: presentation ? 1 : 2, flexWrap: 'wrap', rowGap: 1 }}
        >
            <TextField
                size="small"
                placeholder="Rechercher (identifiant, adresse, commune)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search fontSize="small" />
                        </InputAdornment>
                    ),
                }}
                sx={{ minWidth: 280, flex: 1 }}
            />

            {patrimoineOptions.length > 1 && (
                <Autocomplete
                    multiple
                    size="small"
                    disableCloseOnSelect
                    limitTags={1}
                    options={[ALL_PATRIMOINES, ...patrimoineOptions]}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                    filterOptions={(options, { inputValue }) => {
                        const q = inputValue.trim().toLowerCase();
                        return q ? options.filter((o) => o.id !== ALL_PATRIMOINES.id && o.label.toLowerCase().includes(q)) : options;
                    }}
                    value={patrimoineValue}
                    onChange={onPatrimoinesChange}
                    renderOption={(props, option, { selected }) => (
                        option.id === ALL_PATRIMOINES.id ? (
                            <li {...props} key={option.id} style={{ fontWeight: 600, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                                <Checkbox
                                    size="small"
                                    checked={allPatrimoinesChecked}
                                    indeterminate={!allPatrimoinesChecked && patrimoineValue.length > 0}
                                    sx={{ mr: 1, p: 0 }}
                                />
                                {allPatrimoinesChecked ? 'Tout décocher' : 'Tout cocher'}
                            </li>
                        ) : (
                            <li {...props} key={option.id}>
                                <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0 }} />
                                {option.label}
                            </li>
                        )
                    )}
                    renderTags={(value, getTagProps) => (
                        value.length === patrimoineOptions.length
                            ? <Typography variant="body2" sx={{ pl: 0.5 }}>Tous ({value.length})</Typography>
                            : value.length > 1
                                ? <Typography variant="body2" sx={{ pl: 0.5 }}>{value.length} patrimoines</Typography>
                                : value.map((o, i) => <Chip size="small" label={o.label} {...getTagProps({ index: i })} key={o.id} />)
                    )}
                    renderInput={(params) => <TextField {...params} label={`Patrimoines (${patrimoineOptions.length})`} />}
                    sx={{ minWidth: 260 }}
                />
            )}

            {dechetOptions.length > 0 && (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary">Déchet&nbsp;:</Typography>
                    <ToggleButtonGroup
                        size="small"
                        value={filters.dechetTypes ?? []}
                        onChange={(_e, val) => dispatch(setDechetTypesFilter(val))}
                    >
                        {dechetOptions.map((opt) => (
                            <ToggleButton key={opt.code} value={opt.code}>
                                {opt.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Stack>
            )}

            {conteneurOptions.length > 0 && (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary">Conteneur&nbsp;:</Typography>
                    <ToggleButtonGroup
                        size="small"
                        value={filters.conteneurTypes ?? []}
                        onChange={(_e, val) => dispatch(setConteneurTypesFilter(val))}
                    >
                        {conteneurOptions.map((opt) => (
                            <ToggleButton key={opt.code} value={opt.code}>
                                {opt.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Stack>
            )}

            <Box sx={{ flexGrow: 1 }} />

            <ToggleButtonGroup
                size="small"
                exclusive
                value={mode}
                onChange={(_e, val) => { if (val) dispatch(setMode(val)); }}
                aria-label="Mode d'affichage des statuts"
            >
                <ToggleButton value="lavage" aria-label="Statut du dernier lavage" sx={{ gap: 0.5, textTransform: 'none' }}>
                    <Opacity fontSize="small" /> Lavage
                </ToggleButton>
                <ToggleButton value="checklist" aria-label="État du dernier acte checklist" sx={{ gap: 0.5, textTransform: 'none' }}>
                    <Build fontSize="small" /> Maintenance
                </ToggleButton>
            </ToggleButtonGroup>

            <ToggleButtonGroup
                size="small"
                exclusive
                value={panelMode}
                onChange={(_e, val) => { if (val) dispatch(setPanelMode(val)); }}
            >
                <ToggleButton value="list" aria-label="Liste seule">
                    <ViewList fontSize="small" />
                </ToggleButton>
                <ToggleButton value="both" aria-label="Liste + Carte">
                    <ViewAgenda fontSize="small" />
                </ToggleButton>
                <ToggleButton value="map" aria-label="Carte seule">
                    <MapIcon fontSize="small" />
                </ToggleButton>
            </ToggleButtonGroup>

            {onTogglePresentation && (
                <ToggleButton
                    size="small"
                    value="presentation"
                    selected={presentation}
                    onChange={onTogglePresentation}
                    aria-label={presentation ? 'Quitter le mode présentation (Échap)' : 'Mode présentation (plein écran)'}
                    title={presentation ? 'Quitter (Échap)' : 'Mode présentation'}
                >
                    {presentation ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                </ToggleButton>
            )}
        </Stack>

        {mode === 'lavage' && <LavageStatutFilter rows={rowsPatrimoines} />}

        {mode === 'checklist' && actes.length > 0 && (
            <ChecklistToolbar rows={rowsPatrimoines} actes={actes} acteCourant={acteCourant} />
        )}
        </>
    );
}

export default ConteneurMapToolbar;
